<template>
  <div ref="main" class="question-component">
    <div class="question-container">
      <div
        v-if="question && question.image_large"
        class="question-image-container"
      >
        <QuestionImage
          v-if="image"
          key="q-img"
          class="question-image"
          :src="image"
          transition="fade"
        />
      </div>
      <div v-if="question" class="question-data">
        <h2>
          Frage #{{ question.id }}
          <span v-if="canShare" class="share" @click="share(text.question_text)"
            ><img src="@/assets/share.png"
          /></span>
        </h2>
        <p v-if="testMode" class="question-text">
          {{ text.question_text | clean }}
        </p>
        <p
          v-else
          class="question-text"
          v-html="$options.filters.dictionary(text.question_text)"
        ></p>
        <div v-if="question.answers" class="answers-container">
          <h2 class="answer">Antwortmöglichkeiten</h2>
          <div :class="[solved ? 'solved' : '', 'answer-container']">
            <StaggeredFadeTransition tag="ul" :delay="250">
              <li
                v-for="(a, index) of answers"
                :key="a.num"
                :data-index="index"
              >
                <CustomCheckbox
                  v-model="checked"
                  :num="a.num"
                  :correct="a.is_correct === '1' ? true : false"
                  :solved="solved"
                  :solving="solving"
                >
                  <span v-if="testMode">{{ a.answer_text | clean }}</span>
                  <span
                    v-else
                    v-html="$options.filters.dictionary(a.answer_text)"
                  ></span>
                </CustomCheckbox>
              </li>
            </StaggeredFadeTransition>
          </div>
          <BaseButton
            v-if="hasEasyMode"
            :class="[next ? 'disabled' : '', 'll-button easy']"
            role="button"
            down-arrows
            wide
            @click="doEasyMode"
          >
            Frage leichter verständlich
          </BaseButton>
          <div v-if="next" class="solve-button-container" @click="finish">
            <BaseButton>
              <span>Weiter</span>
            </BaseButton>
          </div>
          <div v-else class="solve-button-container">
            <BaseButton
              class="solve-button"
              role="button"
              arrows
              @click="solve(false)"
            >
              <span v-if="result === true" class="correct"> Richtig! </span>
              <span v-if="result === false" class="wrong"> Falsch! </span>
              <span v-if="!solved && result === null">
                <template v-if="testMode"> Weiter </template>
                <template v-else> Frage lösen </template>
              </span>
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
    <transition name="slide-up" @enter="scrollToEasyMode">
      <div
        v-show="hasEasyMode && doingEasyMode"
        class="question-container easy"
      >
        <div
          v-if="question && question.image_large && image"
          class="question-image-container"
        >
          <Transition name="appear" appear>
            <video
              v-if="image_ll"
              :poster="image"
              class="question-image"
              controls
            >
              <source :src="image_ll" type="video/mp4" />
            </video>
            <div v-else>
              <QuestionImage
                v-if="image"
                key="q-img"
                class="question-image"
                :src="image"
                transition="fade"
              />
            </div>
          </Transition>
        </div>
        <div class="question-data easy">
          <h2 ref="easy">
            Leicht erklärt
            <span
              v-if="canShare"
              class="share"
              @click="share(text.question_text_ll)"
              ><img src="@/assets/share.png"
            /></span>
          </h2>
          <p class="question-text">
            <span
              v-if="text.question_text_ll"
              v-html="$options.filters.dictionary(text.question_text_ll)"
            ></span>
            <span
              v-else
              v-html="$options.filters.dictionary(text.question_text)"
            ></span>
          </p>
          <div v-if="question.answers" class="answers-container">
            <h2 class="answer">Antwortmöglichkeiten</h2>
            <div :class="[solved ? 'solved' : '', 'answer-container']">
              <StaggeredFadeTransition tag="ol" :delay="250">
                <li
                  v-for="(a, index) of answers"
                  :key="a.num"
                  :data-index="index"
                >
                  <CustomCheckbox
                    v-model="checked_ll"
                    :num="a.num"
                    :correct="a.is_correct === '1' ? true : false"
                    :solved="solved"
                    :solving="solving"
                  >
                    {{ index + 1 }}.
                    <span
                      v-if="a.answer_text_ll"
                      v-html="$options.filters.dictionary(a.answer_text_ll)"
                    ></span>
                    <span
                      v-else
                      v-html="$options.filters.dictionary(a.answer_text)"
                    ></span>
                  </CustomCheckbox>
                </li>
              </StaggeredFadeTransition>
            </div>
            <div v-if="next" class="solve-button-container" @click="finish">
              <BaseButton>
                <span>Weiter</span>
              </BaseButton>
            </div>
            <div v-else class="solve-button-container">
              <BaseButton arrows @click="solve(true)">
                <span v-if="result_ll === true" class="correct">
                  Richtig!
                </span>
                <span v-if="result_ll === false" class="wrong"> Falsch! </span>
                <span v-if="!solved_ll">Frage lösen</span>
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "@/http";
import qs from "qs";
import VueScrollTo from "vue-scrollto";
import { mapGetters, mapActions } from "vuex";
import success from "@/assets/success-car-start-short.mp3";
import fail from "@/assets/fail-horn.mp3";
import StaggeredFadeTransition from "@/components/StaggeredFadeTransition.vue";
import CustomCheckbox from "@/components/CustomCheckbox.vue";
import QuestionImage from "@/components/QuestionImage.vue";
import BaseButton from "@/components/BaseButton.vue";

let audioSuccess = new Audio(success);
let audioFail = new Audio(fail);

export default {
  name: "Question",
  components: {
    StaggeredFadeTransition,
    CustomCheckbox,
    QuestionImage,
    BaseButton,
  },
  props: {
    question: {
      type: Object,
      default: () => {
        return {};
      },
    },
    testMode: {
      type: Boolean,
      default: false,
    },
    testId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      language: "DE",
      checked: [],
      checked_ll: [],
      result: null,
      result_ll: null,
      loadedImage: null,
      solving: false,
      doingEasyMode: false,
      next: false,
      correct: false,
      startTime: null,
      easyStartTime: null,
    };
  },
  computed: {
    answers() {
      return this.question.answers[this.language];
    },
    text() {
      return this.question.text[this.language];
    },
    solved() {
      if (this.result !== null && this.hasEasyMode && !this.solved_ll) {
        return false;
      } else {
        return this.result !== null;
      }
    },
    solved_ll() {
      return this.result_ll !== null;
    },
    hasEasyMode() {
      if (this.testMode) return false;
      if (
        this.text.hasOwnProperty("question_text_ll") &&
        this.text.question_text_ll
      )
        return true;
      if (
        this.question.hasOwnProperty("image_small_ll") &&
        this.question.image_small_ll
      )
        return true;
      if (
        this.question.hasOwnProperty("image_large_ll") &&
        this.question.image_large_ll
      )
        return true;
      for (let a in this.answers) {
        if (a.hasOwnProperty("answer_text_ll") && a.answer_text_ll) return true;
      }

      return false;
    },
    answer_key() {
      let keys = [];
      for (let a of this.question.answers[this.language]) {
        if (this.checked.includes(a.num)) {
          keys.push(1);
        } else {
          keys.push(0);
        }
      }

      return keys.join();
    },
    answer_key_ll() {
      let keys = [];
      for (let a of this.question.answers[this.language]) {
        if (this.checked_ll.includes(a.num)) {
          keys.push(1);
        } else {
          keys.push(0);
        }
      }

      return keys.join();
    },
    image() {
      let img = new Image();
      let vm = this;
      img.onload = () => {
        vm.handleLoad();
      };
      img.src =
        process.env.VUE_APP_FLL_ROOT_API +
        "/media/" +
        this.question.image_large;
      if (this.loadedImage !== null) {
        return this.loadedImage;
      } else {
        return null;
      }
    },
    image_ll() {
      if (this.question.image_large_ll) {
        return (
          process.env.VUE_APP_FLL_ROOT_API +
          "/videos/" +
          this.question.image_large_ll
        );
      }
      return false;
    },
    canShare() {
      return !this.testMode && window.navigator.share !== undefined;
    },
    ...mapGetters(["tokenHeader", "mute"]),
  },
  filters: {
    clean(value) {
      return value.toString().replace(/\[|\]/gi, "");
    },
  },
  mounted() {
    this.$emit("loaded");
    console.log("starting timer");
    this.startTime = Date.now();
  },
  methods: {
    reset() {
      this.scrollToNormalMode();
      this.checked = [];
      this.checked_ll = [];
      this.result = null;
      this.result_ll = null;
      this.loadedImage = null;
      this.solving = false;
      this.doingEasyMode = false;
      this.next = false;
      this.startTime = Date.now();
      this.easyStartTime = null;
      console.log("restarting timer", this.startTime);
      console.log("resetted");
    },
    handleLoad() {
      this.loadedImage =
        process.env.VUE_APP_FLL_ROOT_API +
        "/media/" +
        this.question.image_large;
    },
    backToNormal() {
      this.doingEasyMode = false;
      this.checked = [];
      this.checked_ll = [];
      this.result = null;
      this.easyStartTime = null;
      this.scrollToNormalMode();
    },
    doEasyMode() {
      if (!this.next) {
        this.doingEasyMode = true;
        this.easyStartTime = Date.now();
      }
    },
    scrollToEasyMode() {
      VueScrollTo.scrollTo(this.$refs.easy, 1500, {
        easing: "ease-in-out",
      });
    },
    scrollToNormalMode() {
      VueScrollTo.scrollTo(this.$refs.main, 1500, {
        offset: -30,
        easing: "ease-in-out",
      });
    },
    finish() {
      // stop timer
      let answer_key = this.answer_key;
      this.reset();
      let type = this.correct ? "answered" : "failed";
      this.$emit(type, answer_key);
    },
    done(type) {
      this.next = true;
      if (type === "answered") {
        this.correct = true;
        this.incAnswers();
      } else {
        this.correct = false;
      }
    },
    async playSound(sound) {
      if (!this.mute) {
        sound.play();
      }
    },
    async solve(ll) {
      if (this.solving === true) return;
      ll = ll || false;

      let answer_key = ll ? this.answer_key_ll : this.answer_key;
      if (!this.testMode) {
        let type = null;
        this.solving = true;
        if (answer_key === this.question.answer_key) {
          ll ? (this.result_ll = true) : (this.result = true);
          this.playSound(audioSuccess);
          type = "answered";
        } else {
          ll ? (this.result_ll = false) : (this.result = false);
          this.playSound(audioFail);
          type = "failed";
        }
        let vm = this;
        if (ll) {
          console.log("solving ll");
          if (this.result_ll === true) {
            console.log("ll correct, back to normal");
            this.incEasyAnswerCount();
            this.solving = false;
            this.backToNormal();
          } else {
            console.log("ll wrong, done");
            this.done(type);
          }
        } else {
          console.log("not solving ll");
          if (this.result === false && this.result_ll === null) {
            if (this.hasEasyMode) {
              console.log("ll not yet done, doing easy mode");
              this.solving = false;
              this.doEasyMode();
            } else {
              console.log("failed, no easy mode available");
              type = "failed";
              this.done(type);
            }
          } else {
            console.log("ll already tried, done");
            if (type === "answered") {
              console.log("correct");
              this.incAnswerCount();
            }
            this.done(type);
          }
        }
      }
      try {
        let start = this.doingEasyMode ? this.easyStartTime : this.startTime;
        let time = Date.now() - start;
        let testQuestionId = this.question.hasOwnProperty("test_question_id")
          ? this.question.test_question_id
          : null;
        let postData = qs.stringify({
          answer_key: answer_key,
          ll: ll,
          time: time,
          test_question_id: this.question.test_question_id,
        });
        let result = await axios.post(
          process.env.VUE_APP_FLL_ROOT_API +
            "/questions/" +
            this.question.id +
            "/solve",
          postData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              ...this.tokenHeader,
            },
          }
        );
        if (result && result.data && result.data === "unlocked") {
          this.setPopupMessageTitle("Trophäe erreicht!");
          this.setPopupMessage(
            "Herzlicken Glückwunsch! Du hast die Trophäe für dieses Thema erreicht!"
          );
        }
      } catch (error) {
        // TODO: handle error
        console.error(error);  // eslint-disable-line
      }
      if (this.testMode) {
        this.correct = answer_key === this.question.answer_key;
        this.finish();
      }
    },
    show_dict() {
      console.log("span click");
    },
    share(text) {
      if (window.navigator.share) {
        window.navigator
          .share({
            title: "F-Easy",
            text: text,
            url:
              "https://frontend.fll.dev.sourcenova.at/frage/" +
              this.question.id,
          })
          .then(() => console.log("Successful share"))
          .catch((error) => console.log("Error sharing", error));
      }
    },
    ...mapActions([
      "incAnswerCount",
      "incEasyAnswerCount",
      "incAnswers",
      "setPopupMessage",
      "setPopupMessageTitle",
    ]),
  },
};
</script>

<style lang="scss" scoped>
.question-component {
  position: relative;

  .question-container {
    min-height: 100vh;
    width: 100%;

    &.easy {
      margin-top: 100px;
    }
  }

  .question-image-container {
    min-height: 256px;
    width: 100%;
    overflow: hidden;
    display: flex;
    justify-content: space-around;
    align-items: center;

    img.question-image {
      min-height: 256px;
      width: auto;
      object-fit: contain;
    }

    video.question-image {
      width: 100%;
      height: auto;
      /*height: 300px;*/
      /*width: auto;*/
      user-select: none;
      object-fit: contain;

      &:focus {
        border: none;
        outline: none;
      }
    }
  }

  .question-data {
    padding: $default-container-padding;

    h2 {
      color: $green;
      font-size: 16px;
      margin-bottom: 10px;
      display: flex;
      align-items: center;

      span.share {
        margin-left: auto;
        cursor: pointer;

        img {
          width: 24px;
          height: auto;
        }
      }

      &.answer {
        margin-bottom: 15px;
      }
    }

    ul,
    ol {
      margin: 0;
      padding: 0;

      li {
        list-style-type: none;
        cursor: pointer;
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-bottom: 5px;
        color: red;

        p {
          margin: 0 0 0 10px;
        }

        /*.checkbox-container {*/
        /*display: block;*/
        /*width: 22px;*/
        /*height: 22px;*/
        /*cursor: pointer;*/
        /*flex-shrink: 0;*/
        /*position: relative;*/

        /*input[type="checkbox"] {*/
        /*position: absolute;*/
        /*top: 0;*/
        /*left: 0;*/
        /*opacity: 0;*/
        /*z-index: 2;*/
        /*width: 100%;*/
        /*height: 100%;*/
        /*margin: 0;*/
        /*padding: 0;*/
        /*cursor: pointer;*/

        /*&:checked ~ .custom-check {*/
        /*background-color: $green;*/
        /*}*/
        /*}*/

        /*.custom-check {*/
        /*position: absolute;*/
        /*transition: all 0.3s ease-in-out;*/
        /*top: 0;*/
        /*left: 0;*/
        /*border: 1px solid $blue;*/
        /*display: inline-block;*/
        /*width: 100%;*/
        /*max-width: 22px;*/
        /*height: 22px;*/
        /*z-index: 1;*/
        /*cursor: pointer;*/
        /*}*/
        /*}*/
      }
    }

    .answer-container {
      user-select: none;

      li {
        margin: 10px 0;
      }
    }

    /*.answer-container.solved {*/
    /*ul {*/
    /*li {*/
    /*.checkbox-container {*/
    /*.custom-check {*/
    /*&.correct {*/
    /*background-color: $green;*/
    /*}*/

    /*&.incorrect {*/
    /*background-color: $red;*/
    /*}*/
    /*}*/
    /*}*/
    /*}*/
    /*}*/
    /*}*/

    .ll-button.easy {
      margin-top: 20px;
    }

    .solve-button-container {
      margin-top: 20px;
      display: flex;
      justify-items: flex-end;

      .ll-button {
        margin-left: auto;
      }
    }
  }

  .result-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-around;
    align-items: center;
    font-size: 25px;
    background: #000000db;
    z-index: 3;
    cursor: pointer;

    p {
      transition: all 0.5s ease-in-out;

      &.correct {
        color: #00ff00;
      }

      &.wrong {
        color: #ff0000;
      }
    }
  }
}

.appear-leave-active,
.appear-enter-active {
  transition: 0.3s;
  position: absolute;
}
.appear-enter {
  opacity: 0;
}
.appear-enter-to {
  opacity: 1;
}
.appear-leave {
  opacity: 1;
}
.appear-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 1s;
  position: absolute;
}
.slide-enter,
.slide-leave-to {
  opacity: 0;
  left: -100%;
}
.slide-enter-to,
.slide-leave {
  opacity: 1;
  left: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 1s;
  /*position: absolute;*/
}
.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  /*top: 100%;*/
}
.slide-up-enter-to,
.slide-up-leave {
  opacity: 1;
  /*top: 0;*/
}
</style>
